import { computed, toValue, type ComputedRef, type MaybeRefOrGetter } from 'vue'
import { fieldControlUi } from '../base/fieldControlUi'
import { fieldControlInput } from '../base/fieldControlInput'
import { omitEmptyStringProps } from '../base/omitEmptyStringProps'
import { useFieldHelp } from './FieldHelp/useFieldHelp'
import { fieldPresentationDefaults } from './fieldPresentationDefaults'

/** Shared field verdict, help accessibility and vendor control outline. */
export const useFieldPresentation = (
  source: {
    error: MaybeRefOrGetter<string>
    help: MaybeRefOrGetter<string>
    serverError: MaybeRefOrGetter<string>
    required?: MaybeRefOrGetter<boolean>
  },
  skin: typeof fieldControlUi.control | typeof fieldControlUi.pinInput = fieldControlUi.control
): ReturnType<typeof useFieldHelp> & {
  message: ComputedRef<string>
  errorBinding: ComputedRef<ReturnType<typeof omitEmptyStringProps<{ error: string }>>>
  controlUi: ComputedRef<{ base: string[]; root: string }>
  inputAttributes: ComputedRef<ReturnType<typeof fieldControlInput.attributes>>
} => {
  const { hasHelp, helpBinding } = useFieldHelp(() => toValue(source.help))
  // A server verdict takes precedence over the client verdict for the same field.
  const message = computed(() => {
    const serverError = toValue(source.serverError)
    return serverError === '' ? toValue(source.error) : serverError
  })
  // Nuxt UI treats error="" as true, so a valid field omits the binding.
  const errorBinding = computed(() => omitEmptyStringProps({ error: message.value }))
  const controlUi = computed(() => ({
    base: [skin.base, message.value === '' ? skin.outline : skin.outlineInvalid],
    root: skin.root
  }))
  const inputAttributes = computed(() =>
    fieldControlInput.attributes(toValue(source.required ?? fieldPresentationDefaults.required))
  )
  return { hasHelp, helpBinding, message, errorBinding, controlUi, inputAttributes }
}
